import { useContext, useState } from 'react'
import { v4 as uuid } from 'uuid'
import firebase from '../../../firebase/compat'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { db, timestamp } from '../../../firebase/config'
import { client } from '../../../hooks/Client'
import { Auth } from '../../../StateManagment/Auth'
import { useFirestoreGeneral } from '../../../firebase/useFirestore'
import { computeProductImpact } from '../../../helpers/impactInBeeld/computeProductImpact'
import { parsePrice } from '../../../helpers/impactInBeeld/parsePrice'
import { uniqueSlug } from '../../../helpers/impactInBeeld/slugify'
import formatEuro from '../../../helpers/formatEuro'
import Modal from '../../common/Modal'
import DeleteModal from '../../common/DeleteModal'
import Table from '../../common/table/Table.jsx'
import TableCell from '../../common/table/TableCell.jsx'
import ProductForm from '../ProductForm.jsx'

/**
 * @typedef {object} Product
 * @property {string} ID - Business key
 * @property {string} docid - Firestore document id (added by useFirestore)
 * @property {string} Name - Product name
 * @property {number} Price - Price in euros
 * @property {string} Slug - URL slug (generated once at creation)
 * @property {string} OpeningLine - Optional opening line for the product page
 * @property {string} MicroStory - Optional micro story for the product page
 */

/**
 * Producten & diensten tab: catalogus-CRUD. Impact per product is nooit
 * opgeslagen — altijd prijs × org-impact-per-euro (meting is de enige bron).
 * See docs/features/impact-in-beeld-hub.md.
 *
 * @param {object} props
 * @param {ReturnType<typeof import('../../../hooks/useImpactPerEuroRatio').default>} props.impactPerEuro - Kengetal-resultaat, éénmalig opgehaald door de hub
 */
const ProductsTab = ({ impactPerEuro }) => {
    const [auth] = useContext(Auth)

    /** @type {Array<Product>} */
    const products = useFirestoreGeneral('Products', 'CompagnyID', client)
    const { ratio, result: ratioResult, error: ratioError, errorMessage: ratioErrorMessage } = impactPerEuro

    const [modalOpen, setModalOpen] = useState(false)
    const [editProduct, setEditProduct] = useState(/** @type {Product | null} */ (null))
    const [name, setName] = useState('')
    const [priceInput, setPriceInput] = useState('')
    const [openingLine, setOpeningLine] = useState('')
    const [microStory, setMicroStory] = useState('')
    const [formError, setFormError] = useState('')

    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('')

    const openCreateModal = () => {
        setEditProduct(null)
        setName('')
        setPriceInput('')
        setOpeningLine('')
        setMicroStory('')
        setFormError('')
        setModalOpen(true)
    }

    const openEditModal = (product) => {
        setEditProduct(product)
        setName(product.Name || '')
        setPriceInput(product.Price !== undefined && product.Price !== null ? String(product.Price) : '')
        setOpeningLine(product.OpeningLine || '')
        setMicroStory(product.MicroStory || '')
        setFormError('')
        setModalOpen(true)
    }

    const saveProduct = async () => {
        const trimmedName = name.trim()
        const price = parsePrice(priceInput)

        if (!trimmedName || price === null) {
            setFormError('Vul een naam en een geldige prijs in.')
            return
        }

        const fields = {
            Name: trimmedName,
            Price: price,
            OpeningLine: openingLine.trim(),
            MicroStory: microStory.trim(),
        }

        try {
            if (editProduct) {
                // Geen Timestamp in de update: het veld blijft de aanmaakdatum
                // (module-level timestamp zou hier een stale waarde schrijven —
                // AI-CONTEXT: See docs/pitfalls.md#state-stale-module-level-timestamp).
                // ModifiedAt voedt de 'Actie nodig'-detectie op gepubliceerde uitingen.
                await db.collection('Products').doc(editProduct.docid).update({
                    ...fields,
                    ModifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
                })
            } else {
                // Slug-uniciteit moet ook soft-verwijderde producten meetellen: een
                // slug is het permanente publieke adres van een product en mag na
                // verwijderen nooit hergebruikt worden. De centrale hook filtert
                // Deleted weg, dus hier een one-shot read over ALLE docs.
                // AI-CONTEXT: See docs/pitfalls.md#database-key-missing-scope-dimension
                const allProducts = await db.collection('Products')
                    .where('CompagnyID', '==', client)
                    .get()
                const existingSlugs = allProducts.docs
                    .map((doc) => doc.data().Slug)
                    .filter(Boolean)

                const nextPosition = (products || [])
                    .reduce((max, product) => Math.max(max, Number(product.Position) || 0), 0) + 1

                await db.collection('Products').add({
                    ...fields,
                    ID: uuid(),
                    CompagnyID: client,
                    Compagny: client,
                    Position: nextPosition,
                    Timestamp: timestamp,
                    Slug: uniqueSlug(trimmedName, existingSlugs),
                })
            }
            setModalOpen(false)
        } catch (error) {
            alert(error)
        }
    }

    const openDeleteModal = (product) => {
        setDeleteDocid(product.docid)
        setDeleteName(product.Name)
        setDeleteModal(true)
    }

    // Soft delete (ADR-008) — the central useFirestore filter hides the row.
    const deleteProduct = () => {
        db.collection('Products').doc(deleteDocid).update({
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        })
        .catch((error) => alert(error))
    }

    const impactCell = (product) => {
        const impact = computeProductImpact(product.Price, ratio)
        if (impact === null) return '—'
        return formatEuro(impact)
    }

    const columns = [
        { key: 'name', label: 'Naam' },
        { key: 'price', label: 'Prijs' },
        { key: 'impact', label: 'Impact per verkoop' },
        { key: 'actions', label: '' },
    ]

    // Client-side sorteren — geen orderBy op een (optioneel) veld.
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
    const sortedProducts = [...(products || [])]
        .sort((a, b) => (Number(a.Position) || 0) - (Number(b.Position) || 0))

    return (
        <div className='impact-in-beeld-tab'>
            <h2>Producten &amp; diensten</h2>
            <p className='impact-in-beeld-tab-intro'>
                Beheer hier je producten en diensten. De impact per product volgt altijd uit je meting:
                prijs × impact per euro.
            </p>
            {ratioError && (
                <p className='impact-in-beeld-form-error'>{ratioErrorMessage}</p>
            )}
            {!ratioError && ratio === null && (
                <p className='impact-in-beeld-tab-notice'>
                    {ratioResult?.status === 'onvoldoende' && ratioResult.reden
                        ? ratioResult.reden
                        : 'Vul je jaarlijkse kosten in bij Instellingen → Algemeen om de impact per product te tonen.'}
                </p>
            )}
            {(products?.length || 0) === 0 ? (
                <div className='impact-in-beeld-empty-state'>
                    <p>Je hebt nog geen producten of diensten toegevoegd. Voeg je eerste product toe om
                    de impact per verkoop te laten zien.</p>
                    <button onClick={openCreateModal}>+ Product toevoegen</button>
                </div>
            ) : (
                <>
                    <Table
                        columns={columns}
                        items={sortedProducts}
                        showSearch={false}
                        renderRow={(product) => (
                            <>
                                <TableCell>{product.Name}</TableCell>
                                <TableCell>{formatEuro(product.Price)}</TableCell>
                                <TableCell>{impactCell(product)}</TableCell>
                                <TableCell className='impact-in-beeld-product-actions'>
                                    <button
                                        type='button'
                                        aria-label={`Bewerk ${product.Name}`}
                                        onClick={() => openEditModal(product)}
                                    >
                                        <EditOutlinedIcon fontSize='small' />
                                    </button>
                                    <button
                                        type='button'
                                        aria-label={`Verwijder ${product.Name}`}
                                        onClick={() => openDeleteModal(product)}
                                    >
                                        <DeleteOutlineOutlinedIcon fontSize='small' />
                                    </button>
                                </TableCell>
                            </>
                        )}
                    />
                    <button className='impact-in-beeld-add-button' onClick={openCreateModal}>
                        + Product toevoegen
                    </button>
                </>
            )}
            <Modal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                confirm={saveProduct}
                pageHeader={editProduct ? 'Product bewerken' : 'Product toevoegen'}
                content={
                    <ProductForm
                        name={name}
                        setName={setName}
                        priceInput={priceInput}
                        setPriceInput={setPriceInput}
                        openingLine={openingLine}
                        setOpeningLine={setOpeningLine}
                        microStory={microStory}
                        setMicroStory={setMicroStory}
                        formError={formError}
                    />
                }
            />
            <DeleteModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                deleteItem={deleteProduct}
                deleteName={deleteName}
                softDelete
              />
        </div>
    )
}

export default ProductsTab
